import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PieChart, SquarePlus, type LucideIcon } from 'lucide-react';
import * as P from '@/constants/paths';
import { parseQueryParams } from '@/utils/QueryParamsRoutes/queryParamsUtils';
import { LoadingPage } from '@/components/screens/LoadingPage';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { FullScreenPageContainer } from '@/components/utils/FullScreenPageContainer';
import { useContextHydration } from '@/hooks/context/useContextHydration';
import { useContextStore } from '@/hooks/context/useContextStore';
import { useLatestContext } from '@/hooks/context/useLatestContext';
import useRegisterStore from '@/hooks/useRegistrationStore';

const EXPERIENCE_ICONS: { consumer: LucideIcon; org: LucideIcon } = {
  consumer: PieChart,
  org: SquarePlus,
};

const ExperienceCard = ({
  icon: Icon,
  title,
  description,
  badge,
  onClick,
  testId,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
  testId: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex w-full items-center justify-between gap-4 rounded-xl border border-stroke-primary-subtle bg-background p-6 text-left transition-colors hover:border-stroke-primary-bold"
  >
    <div className="flex items-center gap-4">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-overlay-primary">
        <Icon aria-hidden size={22} className="text-primary" />
      </span>
      <div className="flex flex-col gap-1">
        <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-3">
          <span className="font-semilight text-xl/snug md:text-[22px]/snug lg:text-2xl/snug text-primary">
            {title}
          </span>
          {badge && (
            <span className="rounded bg-overlay-primary px-2 py-0.5 font-normal text-xxs/snug md:text-xs/snug uppercase whitespace-nowrap text-primary">
              {badge}
            </span>
          )}
        </div>
        <span className="font-light text-sm/snug lg:text-base/snug text-secondary">
          {description}
        </span>
      </div>
    </div>
    <span aria-hidden className="typography-h4 text-secondary">
      →
    </span>
  </button>
);

export const ExperiencePickerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Post-selection destination (e.g. after a patient registration we forward
  // to the questionnaire). Defaults to home for the normal post-login flow.
  const { redirect_path: redirectPath, reselect } = parseQueryParams(
    location.search,
  );
  const destination = redirectPath || P.HOME_PATH;
  // DE-702: explicit re-pick from the "Change context" banner. The user is
  // already in a context, so the post-registration auto-switch below would
  // otherwise bounce them straight back into it (→ home). Force the picker.
  const forceReselect = reselect === 'true' || reselect === '1';
  const { isLoading, isError, orgs, consumerExists, needsPicker } =
    useContextHydration();
  const patientOrg = useContextStore((s) => s.patientOrg);
  const proKitOrg = useRegisterStore((s) => s.proKitOrg);
  const selectConsumer = useContextStore((s) => s.selectConsumer);
  const selectOrg = useContextStore((s) => s.selectOrg);
  // DE-702: badge the context holding the latest test results.
  const { hasAnyResults, latestContextKey } = useLatestContext();
  // Guards the one-shot auto-resolution. `selectOrg`/`selectConsumer` set a new
  // `patientOrg` object, which changes `pendingOrg` and would otherwise re-fire
  // this effect forever (React "maximum update depth" / #185).
  const hasResolvedRef = useRef(false);

  // Post-PRO-registration: `patientOrg` is set at success (sessionStorage).
  // Only fall back to persisted `proKitOrg` on an explicit post-registration
  // redirect — never on a normal login/picker visit, or a stale localStorage
  // value will auto-select and skip the multi-experience choice.
  const pendingOrgSource =
    patientOrg ?? (redirectPath ? proKitOrg : null);
  const pendingOrg =
    pendingOrgSource &&
    orgs.some((o) => o.org.id === pendingOrgSource.id)
      ? pendingOrgSource
      : null;
  // Just-registered org not in refreshed content yet — wait; do not fall back
  // to consumer (wrong questionnaire).
  const awaitingPendingOrg = !!pendingOrgSource && !pendingOrg;

  useEffect(() => {
    if (isLoading || isError || hasResolvedRef.current) return;
    // Explicit re-pick: skip every auto-resolution so the picker always shows
    // when both experiences exist. Nothing to pick (single experience) → home.
    if (forceReselect) {
      if (!needsPicker) {
        hasResolvedRef.current = true;
        navigate(P.HOME_PATH, { replace: true });
      }
      return;
    }
    // Post-registration: auto-switch into the just-registered org.
    if (pendingOrg) {
      hasResolvedRef.current = true;
      selectOrg(pendingOrg.id);
      navigate(destination, { replace: true });
      return;
    }
    // Just-registered org not in content yet -> wait (don't drop to consumer).
    if (awaitingPendingOrg) return;
    // Both experiences (and nothing pending) -> let the user pick.
    if (needsPicker) return;
    // Exactly one experience -> select it and go straight in.
    hasResolvedRef.current = true;
    if (orgs.length >= 1 && !consumerExists) {
      selectOrg(orgs[0].org.id);
    } else {
      selectConsumer();
    }
    navigate(destination, { replace: true });
  }, [
    isLoading,
    isError,
    needsPicker,
    consumerExists,
    orgs,
    pendingOrg,
    awaitingPendingOrg,
    selectConsumer,
    selectOrg,
    navigate,
    destination,
    forceReselect,
  ]);

  if (isLoading) return <LoadingPage />;
  if (isError) return <ErrorLoadingPage errorType="logout" />;
  // Show the picker only for a genuine "both" choice; otherwise the effect
  // above is navigating (single experience / auto-switch) or waiting for a
  // just-registered org to appear in content. On an explicit re-pick, ignore
  // the pending-org/awaiting gates (the user IS in a context) — show the picker
  // whenever both experiences exist.
  if (
    forceReselect
      ? !needsPicker
      : !needsPicker || pendingOrg || awaitingPendingOrg
  )
    return <LoadingPage />;

  const goConsumer = () => {
    selectConsumer();
    navigate(destination, { replace: true });
  };
  const goOrg = (orgId: number) => {
    selectOrg(orgId);
    navigate(destination, { replace: true });
  };

  return (
    <FullScreenPageContainer hideBackButton data-testid="experiencePicker-container">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <div className="flex flex-col gap-3">
          <span className="typography-caption1 font-semibold uppercase tracking-wide text-accent">
            Welcome back
          </span>
          <h1 className="text-[22px] md:text-[24px] lg:text-3xl text-primary">
            Choose an experience
          </h1>
          <p className="font-light text-sm/snug lg:text-base/snug text-secondary">
            Your email is linked to more than one Viome experience. They stay
            separate — your responses and journey progress in one don&apos;t
            affect the other.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <ExperienceCard
            icon={EXPERIENCE_ICONS.consumer}
            title="Viome consumer"
            description="Your personal account — results, recommendations, and retests you've purchased."
            badge={
              hasAnyResults && latestContextKey === 'consumer'
                ? 'Latest results'
                : undefined
            }
            onClick={goConsumer}
            testId="experiencePicker-consumerCard"
          />
          {orgs.map((entry) => (
            <ExperienceCard
              key={entry.org.id}
              icon={EXPERIENCE_ICONS.org}
              title={entry.org.name}
              badge={
                hasAnyResults && latestContextKey === entry.org.id
                  ? 'Latest results'
                  : undefined
              }
              description="Patient experience invited by your provider."
              onClick={() => goOrg(entry.org.id)}
              testId={`experiencePicker-orgCard-${entry.org.id}`}
            />
          ))}
        </div>
      </div>
    </FullScreenPageContainer>
  );
};
